import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import { useAuth } from "../../contexts/AuthContext";
import { resolveTenantId } from "../../utils/jwtUtils";
import { parseBulkFile } from "../../utils/bulkUpload/parseBulkFile";
import type { BulkEntityConfig } from "../../utils/bulkUpload/bulkEntityConfigs";

type DialogStep = "pick" | "uploading" | "summary";

interface BulkEntityUploadDialogProps {
  open: boolean;
  onClose: () => void;
  onComplete: (hadSuccess: boolean) => void;
  config: BulkEntityConfig;
}

const BulkEntityUploadDialog: React.FC<BulkEntityUploadDialogProps> = ({
  open,
  onClose,
  onComplete,
  config,
}) => {
  const { user } = useAuth();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<DialogStep>("pick");
  const [scanError, setScanError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState(0);
  const [uploadedCount, setUploadedCount] = useState(0);
  const [successes, setSuccesses] = useState<string[]>([]);
  const [failures, setFailures] = useState<{ label: string; error: string }[]>([]);

  const resetState = () => {
    setStep("pick");
    setScanError(null);
    setTotalCount(0);
    setUploadedCount(0);
    setSuccesses([]);
    setFailures([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  useEffect(() => {
    if (open) resetState();
  }, [open]);

  const handleDialogClose = () => {
    if (step === "summary") {
      const hadSuccess = successes.length > 0;
      resetState();
      onClose();
      if (hadSuccess) onComplete(true);
      return;
    }
    if (step !== "uploading") {
      resetState();
      onClose();
    }
  };

  const downloadTemplate = () => {
    const blob = new Blob([config.templateJson], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${config.entityLabel.toLowerCase()}-bulk-template.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const runUpload = async (rows: Record<string, unknown>[]) => {
    const tenantId = resolveTenantId(user?.tenantId);
    if (!tenantId) {
      setScanError(
        "Tenant ID not found. Ensure you are signed in and your session includes a tenant.",
      );
      return;
    }

    setStep("uploading");
    setTotalCount(rows.length);
    setUploadedCount(0);
    const ok: string[] = [];
    const fail: { label: string; error: string }[] = [];

    for (let i = 0; i < rows.length; i++) {
      const row = rows[i];
      const label =
        String(
          row.name ??
            row.gatewayCommonName ??
            row.gatewayProductIdentifier ??
            row.code ??
            row.title ??
            row.appKeyId ??
            `Row ${i + 1}`,
        ) || `Row ${i + 1}`;
      const validationError = config.validateRow(row, i);
      if (validationError) {
        fail.push({ label, error: validationError });
        setUploadedCount(i + 1);
        continue;
      }
      try {
        await config.createRow(row, tenantId);
        ok.push(label);
      } catch (err: unknown) {
        fail.push({
          label,
          error: err instanceof Error ? err.message : "Upload failed",
        });
      }
      setUploadedCount(i + 1);
    }

    setSuccesses(ok);
    setFailures(fail);
    setStep("summary");
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setScanError(null);
    try {
      const rows = await parseBulkFile(file);
      if (!rows.length) {
        setScanError("No rows found in file");
        return;
      }
      await runUpload(rows);
    } catch (err: unknown) {
      setScanError(err instanceof Error ? err.message : "Failed to parse file");
      setStep("pick");
    }
  };

  const progress =
    totalCount > 0 ? Math.round((uploadedCount / totalCount) * 100) : 0;

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="sm" fullWidth>
      <DialogTitle>Bulk upload {config.entityLabel}s</DialogTitle>
      <DialogContent>
        <input
          ref={fileInputRef}
          type="file"
          accept={config.acceptedExtensions}
          hidden
          onChange={handleFileChange}
        />

        {step === "pick" && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Upload a {config.acceptedExtensions} file. Each row creates one{" "}
              {config.entityLabel.toLowerCase()}.
            </Typography>
            {scanError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {scanError}
              </Alert>
            )}
            <Button onClick={downloadTemplate} sx={{ mb: 2, textTransform: "none" }}>
              Download JSON template
            </Button>
          </Box>
        )}

        {step === "uploading" && (
          <Box sx={{ py: 2 }}>
            <Typography variant="body2" gutterBottom>
              Uploading {uploadedCount} of {totalCount}...
            </Typography>
            <LinearProgress variant="determinate" value={progress} />
          </Box>
        )}

        {step === "summary" && (
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {successes.length} succeeded, {failures.length} failed
            </Typography>
            {failures.length > 0 && (
              <List dense>
                {failures.slice(0, 20).map((f) => (
                  <ListItem key={f.label}>
                    <ListItemText
                      primary={f.label}
                      secondary={f.error}
                      primaryTypographyProps={{ color: "error" }}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        {step === "pick" && (
          <>
            <Button onClick={handleDialogClose}>Cancel</Button>
            <Button
              variant="contained"
              onClick={() => fileInputRef.current?.click()}
            >
              Choose file
            </Button>
          </>
        )}
        {step === "summary" && (
          <Button variant="contained" onClick={handleDialogClose}>
            Done
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BulkEntityUploadDialog;
